import { Patient } from '../types/patient';
import { joinFullName } from './patientName';
import { patientCategoryLabel } from './patientCategory';
import {
  composePatientAddress,
  patientAddressDetailRows,
  permanentAddressParts,
  presentAddressParts,
} from './patientAddress';

export interface PatientPrintHospital {
  name: string;
  nameBn?: string;
  logo?: string;
  address?: string;
  email?: string;
  website?: string;
  hotline?: string;
  certified?: string;
  /**
   * Signature & seal assets, shared with invoices. Only used by document
   * printouts when `showSignature` is on (General Settings → Documents tab).
   */
  signature?: string;
  seal?: string;
  signatoryName?: string;
  showSignature?: boolean;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Builds a self-contained, print-ready HTML document for a patient's full
 * profile. Used by the Patient Details "Print" action and kept visually in
 * sync with the backend PDF renderer (`patient-pdf.service.ts`).
 */
export function buildPatientPrintHtml(patient: Patient, hospital: PatientPrintHospital): string {
  const esc = (value: unknown) => escapeHtml(value);
  // Empty fields are omitted entirely, so no label is printed without data.
  const hasValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    return String(value).trim().length > 0;
  };
  const fmtDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };
  const titleCase = (value?: string | null) =>
    value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

  const fullName = joinFullName(patient);
  const age = patient.age ? `${patient.age} years` : '';
  const dob = fmtDate(patient.dateOfBirth);
  const ageDob = [age, dob ? `(${dob})` : ''].filter(Boolean).join(' ');
  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  const permanentParts = permanentAddressParts(patient);
  const permanentAddress = composePatientAddress(permanentParts);
  const presentParts = patient.sameAsPermanent
    ? permanentParts
    : presentAddressParts(patient);
  const presentAddress = patient.sameAsPermanent
    ? 'Same as permanent address'
    : composePatientAddress(presentParts);

  // Structured parts are printed underneath the typed full address: the full
  // address often omits the division/district the registrar selected, and a
  // medical record needs every recorded unit on paper.
  const permanentDetailRows = patientAddressDetailRows(permanentParts);
  const presentDetailRows = patient.sameAsPermanent
    ? []
    : patientAddressDetailRows(presentParts);

  const medicalFlags = [
    { key: 'drugAllergy', label: 'Drug Allergy' },
    { key: 'foodAllergy', label: 'Food Allergy' },
    { key: 'pregnancy', label: 'Pregnancy' },
    { key: 'highRisk', label: 'High Risk' },
    { key: 'infectiousDisease', label: 'Infectious Disease' },
    { key: 'fallRisk', label: 'Fall Risk' },
    { key: 'wheelchair', label: 'Wheelchair' },
    { key: 'hearingImpairment', label: 'Hearing Impairment' },
    { key: 'visualImpairment', label: 'Visual Impairment' },
  ].filter((flag) => (patient as any)[flag.key]);

  const clinicalAlerts = (patient.clinicalAlerts || [])
    .map((entry) => entry.clinicalAlert?.label || entry.clinicalAlert?.name)
    .filter(Boolean) as string[];

  const row = (label: string, value: unknown) =>
    hasValue(value) ? `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>` : '';

  const categoryLabel = patientCategoryLabel(patient.patientCategory);
  const identityCells = [
    { label: 'Age / DOB', value: ageDob },
    { label: 'Age Group', value: titleCase(patient.ageGroup) },
    { label: 'Gender', value: titleCase(patient.gender) },
    { label: 'Husband / Father Name', value: patient.localName },
    { label: 'Blood Group', value: patient.bloodGroup },
    { label: 'Mobile', value: patient.mobileNumber },
    { label: 'Email', value: patient.email },
    { label: 'Category', value: categoryLabel },
    { label: 'Marital Status', value: titleCase(patient.maritalStatus) },
    { label: 'Nationality', value: patient.nationality },
    { label: 'Occupation', value: patient.occupation },
    { label: 'Employer', value: patient.employer },
  ].filter((cell) => hasValue(cell.value));
  // Four boxes per row; every row stretches edge to edge, and each box grows or
  // shrinks with its content, so short values ("O+") stay compact while longer
  // ones get the extra room.
  const CELLS_PER_ROW = 4;
  const renderCell = (cell: { label: string; value: unknown }) =>
    `<div class="cell"><div class="k">${esc(cell.label)}</div><div class="v">${esc(cell.value)}</div></div>`;
  const gridRows: string[] = [];
  for (let i = 0; i < identityCells.length; i += CELLS_PER_ROW) {
    gridRows.push(
      `<div class="grid-row">${identityCells.slice(i, i + CELLS_PER_ROW).map(renderCell).join('')}</div>`,
    );
  }
  const gridHtml = gridRows.length ? `<div class="grid">${gridRows.join('')}</div>` : '';

  // The address is grouped under one "Address" section with a sub-heading per
  // address type, so "Permanent" / "Present" is stated once instead of being
  // repeated on every single row.
  const permanentRows = [
    row('Address', permanentAddress),
    ...permanentDetailRows.map((entry) => row(entry.label, entry.value)),
  ].join('');
  const presentRows = patient.sameAsPermanent
    ? row('Address', 'Same as permanent address')
    : [
        row('Address', presentAddress),
        ...presentDetailRows.map((entry) => row(entry.label, entry.value)),
      ].join('');
  const contactRows = [
    row('Mobile', patient.mobileNumber),
    row('Secondary Mobile', patient.secondaryMobile),
    row('Email', patient.email),
  ].join('');
  const guardianRows = [
    row('Name', patient.guardianName),
    row('Relation', patient.guardianRelation),
    row('Phone', patient.guardianPhone),
    row('Address', patient.guardianAddress),
  ].join('');
  const emergencyRows = [
    row('Name', patient.emergencyContactName),
    row('Relation', patient.emergencyContactRelation),
    row('Phone', patient.emergencyContactPhone),
    row('Alternate Phone', patient.emergencyContactAltPhone),
    row('Address', patient.emergencyContactAddress),
  ].join('');
  const insuranceRows = [
    row('Insurance Company', patient.insuranceCompany),
    row('Policy Number', patient.policyNumber),
    row('Corporate Organization', patient.corporateOrg),
    row('Member ID', patient.memberId),
    row('Insurance Validity', fmtDate(patient.insuranceValidity)),
    row('Coverage', patient.coveragePercent != null ? `${patient.coveragePercent}%` : ''),
    row('Authorization Number', patient.authorizationNumber),
  ].join('');

  // Address always stacks permanent/present, so it can sit in the left column
  // of a two-column row while contact details fill the right column.
  const addressBlock = `<div>
    <h2 class="section">Address</h2>
    <h3 class="subhead">Permanent Address</h3>
    <table>${permanentRows || row('Address', 'Not recorded')}</table>
    <h3 class="subhead spaced">Present Address</h3>
    <table>${
      patient.sameAsPermanent
        ? row('Address', 'Same as permanent address')
        : presentRows || row('Address', 'Not recorded')
    }</table>
  </div>`;
  const contactBlock = contactRows
    ? `<div><h2 class="section">Contact</h2><table>${contactRows}</table></div>`
    : '';
  const guardianBlock = guardianRows
    ? `<div><h2 class="section">Guardian</h2><table>${guardianRows}</table></div>`
    : '';
  const emergencyBlock = emergencyRows
    ? `<div><h2 class="section">Emergency Contact</h2><table>${emergencyRows}</table></div>`
    : '';
  const insuranceBlock = insuranceRows
    ? `<div><h2 class="section">Insurance</h2><table>${insuranceRows}</table></div>`
    : '';

  const identityRows = [
    row('National ID', patient.nationalId),
    row('Passport', patient.passport),
    row('Birth Certificate', patient.birthCertificate),
    row('Driving License', patient.drivingLicense),
    row('Insurance Card', patient.insuranceCardNumber),
  ].join('');
  const identityBlock = identityRows
    ? `<h2 class="section">Identity</h2><table>${identityRows}</table>`
    : '';

  const registrationRows = [
    row('UHID', patient.registrationNumber),
    row('Registration Type', titleCase(patient.registrationType)),
    row('Registered On', fmtDate(patient.registrationDate)),
    row(
      'Registered By',
      patient.createdByEmployee
        ? [patient.createdByEmployee.name, patient.createdByEmployee.employeeCode]
            .filter(Boolean)
            .join(' · ')
        : '',
    ),
    row('Referral Source', patient.referralSource),
    row('Status', patient.isActive ? 'Active' : 'Inactive'),
  ].join('');
  const registrationBlock = registrationRows
    ? `<h2 class="section">Registration</h2><table>${registrationRows}</table>`
    : '';

  const hasMedicalInfo = medicalFlags.length > 0 || hasValue(patient.medicalNote);
  const medicalBlock = hasMedicalInfo
    ? `<h2 class="section">Medical Flags &amp; Notes</h2>
       ${medicalFlags.length > 0
         ? `<div class="flags">${medicalFlags.map((flag) => `<span class="flag">${esc(flag.label)}</span>`).join('')}</div>`
         : ''}
       ${hasValue(patient.medicalNote) ? `<div class="note">${esc(patient.medicalNote)}</div>` : ''}`
    : '';

  const alertsBlock = clinicalAlerts.length > 0
    ? `<h2 class="section">Clinical Alerts</h2>
       <div class="flags">${clinicalAlerts.map((label) => `<span class="alert">${esc(label)}</span>`).join('')}</div>`
    : '';

  // Two balanced columns: address/identity/insurance on the left,
  // contact/emergency/guardian/registration on the right, so the page width is
  // used instead of leaving an empty half under short sections. Long free-text
  // blocks (medical notes, alerts) stay full width so they paginate cleanly.
  const leftColumn = `${addressBlock}${identityBlock}${insuranceBlock}`;
  const rightColumn = `${contactBlock}${emergencyBlock}${guardianBlock}${registrationBlock}`;
  const contentRow =
    leftColumn && rightColumn
      ? `<div class="two-col"><div>${leftColumn}</div><div>${rightColumn}</div></div>`
      : `${leftColumn}${rightColumn}`;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Patient Record ${esc(patient.registrationNumber)}</title>
<style>
  @font-face { font-family: 'SutonnyMJ'; src: url('/fonts/SutonnyMJ.ttf') format('truetype'); font-display: swap; }
  .bn { font-family: 'SutonnyMJ', 'Noto Sans Bengali', 'Arial Unicode MS', Arial, sans-serif; }
  * { box-sizing: border-box; }
  /* Chrome only draws its own date/title header when the page has a margin.
     Keep the page margin at zero and let the page-shell wrapper reproduce the
     visual margin on every printed page instead. */
  @page { size: A4; margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; padding: 0; font-size: 13px; }
  .page-shell { padding: 12mm 10mm; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f2937; padding-bottom: 12px; }
  .hospital { display: flex; gap: 12px; align-items: flex-start; }
  .hospital img { height: 56px; width: 56px; object-fit: contain; }
  .hospital h1 { margin: 0 0 4px; font-size: 20px; }
  .hospital p { margin: 0; font-size: 12px; color: #4b5563; }
  .title { text-align: right; }
  .title h2 { margin: 0; font-size: 22px; letter-spacing: 2px; }
  .title p { margin: 2px 0 0; font-size: 12px; color: #4b5563; }
  /* Two-column profile: identity header + boxes on the left, photo rail on the
     right. The photo starts level with the first row of boxes (not with the
     name), and the status badge sits directly below the photo. */
  .profile { display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 18px; row-gap: 14px; margin: 18px 0; align-items: start; }
  .profile .identity { grid-column: 1; grid-row: 1; }
  .profile .identity .k { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; }
  .profile .identity h3 { margin: 0 0 4px; font-size: 22px; }
  .profile .identity .uhid { font-family: 'Courier New', monospace; font-size: 13px; color: #4b5563; }
  .profile .photo-col { grid-column: 2; grid-row: 2; display: flex; flex-direction: column; align-items: center; gap: 8px; }
  .profile .photo { width: 120px; height: 150px; object-fit: cover; border: 1px solid #d1d5db; border-radius: 6px; }
  .badge { display: inline-block; padding: 2px 10px; border: 1px solid currentColor; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; }
  .badge.active { background: #dcfce7; border-color: #86efac; color: #15803d; }
  .badge.inactive { background: #fee2e2; border-color: #fca5a5; color: #b91c1c; }
  .grid { display: flex; flex-direction: column; gap: 8px; }
  .profile .grid { grid-column: 1; grid-row: 2; }
  .grid-row { display: flex; gap: 8px; justify-content: space-between; }
  .grid .cell { flex: 1 1 auto; min-width: 0; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 10px; }
  .grid .cell .k { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; }
  .grid .cell .v { font-size: 13px; font-weight: 600; margin-top: 2px; overflow-wrap: anywhere; }
  h2.section { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #374151; margin: 22px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
  h3.subhead { font-size: 11px; text-transform: uppercase; letter-spacing: 0.8px; color: #6b7280; margin: 0 0 4px; }
  h3.subhead.spaced { margin-top: 12px; }
  /* Each row is a flex line so a label hugs its value ("Label: value") instead
     of leaving a gap created by the table's fixed label column. */
  table, tbody { display: block; width: 100%; }
  table tr { display: flex; align-items: baseline; gap: 4px; padding: 6px 0; border-bottom: 1px solid #f3f4f6; }
  table th { text-align: left; padding: 0; white-space: nowrap; color: #374151; font-weight: 700; }
  table th::after { content: ':'; }
  table td { padding: 0; flex: 1; min-width: 0; overflow-wrap: anywhere; }
  .two-col { display: flex; gap: 24px; }
  .two-col > div { flex: 1; }
  .flags { display: flex; flex-wrap: wrap; gap: 6px; }
  .flag { padding: 3px 10px; border-radius: 6px; background: #fee2e2; color: #b91c1c; font-size: 11px; font-weight: 600; }
  .alert { padding: 3px 10px; border-radius: 6px; background: #fef3c7; color: #b45309; font-size: 11px; font-weight: 600; }
  .note { margin-top: 8px; padding: 10px; border: 1px solid #e5e7eb; border-radius: 6px; background: #f9fafb; white-space: pre-wrap; }
  .muted { color: #9ca3af; }
  .footer { margin-top: 36px; border-top: 1px solid #e5e7eb; padding-top: 10px; font-size: 11px; color: #6b7280; text-align: center; break-inside: avoid; }
  @media print { body { padding: 0; } h2.section { page-break-after: avoid; } }
</style>
</head>
<body>
  <div class="page-shell">
  <div class="header">
    <div class="hospital">
      ${hospital.logo ? `<img src="${esc(hospital.logo)}" alt="Logo" />` : ''}
      <div>
        <h1>${esc(hospital.name)}</h1>
        ${hospital.nameBn ? `<p class="bn">${esc(hospital.nameBn)}</p>` : ''}
        ${hospital.address ? `<p>${esc(hospital.address)}</p>` : ''}
        ${contact ? `<p>${esc(contact)}</p>` : ''}
      </div>
    </div>
    <div class="title">
      <h2>PATIENT RECORD</h2>
    </div>
  </div>

  <div class="profile">
    <div class="identity">
      <div class="k">Name</div>
      <h3>${esc(fullName)}</h3>
      <div class="uhid">ID: ${esc(patient.registrationNumber)}</div>
    </div>
    ${gridHtml}
    <div class="photo-col">
      ${hasValue(patient.photoUrl) ? `<img class="photo" src="${esc(patient.photoUrl)}" alt="Patient photo" />` : ''}
      <span class="badge ${patient.isActive ? 'active' : 'inactive'}">${patient.isActive ? 'Active' : 'Inactive'}</span>
    </div>
  </div>

  ${contentRow}

  ${medicalBlock}

  ${alertsBlock}

  <div class="footer">
    ${hospital.name ? `<div>${esc(hospital.name)}</div>` : ''}
    ${hospital.certified ? `<div>${esc(hospital.certified)}</div>` : ''}
    <div>Generated on ${new Date().toLocaleString('en-GB')}</div>
  </div>
  </div>
</body>
</html>`;
}

function fmtDate(value?: string | Date | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function patientAddress(patient: Patient): string {
  if (patient.sameAsPermanent) {
    return composePatientAddress(permanentAddressParts(patient));
  }
  return composePatientAddress(presentAddressParts(patient));
}

/**
 * Compact, branded registration slip. Printed from the success modal after a
 * patient is registered; also the browser-print fallback when no printer device
 * is assigned in Settings → Devices.
 */
export function buildRegistrationSlipHtml(patient: Patient, hospital: PatientPrintHospital): string {
  const esc = (value: unknown) => escapeHtml(value);
  const dash = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value).trim();
    return text.length > 0 ? esc(text) : '—';
  };
  const fullName = joinFullName(patient);
  const age = patient.age ? `${patient.age} yrs` : '';
  const ageGender = [age, patient.gender].filter(Boolean).join(' / ') || '—';
  const addr = patientAddress(patient);
  const slipAddressParts = patient.sameAsPermanent
    ? permanentAddressParts(patient)
    : presentAddressParts(patient);
  const slipAddressDetails = patientAddressDetailRows(slipAddressParts);
  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  const row = (label: string, value: unknown) =>
    `<tr><th>${esc(label)}</th><td>${dash(value)}</td></tr>`;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Registration Slip ${esc(patient.registrationNumber)}</title>
<style>
  @font-face { font-family: 'SutonnyMJ'; src: url('/fonts/SutonnyMJ.ttf') format('truetype'); font-display: swap; }
  .bn { font-family: 'SutonnyMJ', 'Noto Sans Bengali', 'Arial Unicode MS', Arial, sans-serif; }
  * { box-sizing: border-box; }
  @page { size: A4; margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; padding: 0; font-size: 13px; }
  .page-shell { padding: 12mm 10mm; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .header { text-align: center; border-bottom: 2px solid #1f2937; padding-bottom: 10px; }
  .header img { height: 52px; width: 52px; object-fit: contain; }
  .header h1 { margin: 4px 0 2px; font-size: 19px; }
  .header p { margin: 0; font-size: 12px; color: #4b5563; }
  .title { margin: 14px 0 10px; text-align: center; font-size: 15px; letter-spacing: 2px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  table th { text-align: left; width: 34%; padding: 6px 8px; vertical-align: top; color: #6b7280; font-weight: 500; border-bottom: 1px solid #f3f4f6; }
  table td { padding: 6px 8px; vertical-align: top; border-bottom: 1px solid #f3f4f6; }
  .uhid { font-family: 'Courier New', monospace; font-weight: 700; }
  .note { margin-top: 6px; padding: 10px; border: 1px dashed #d1d5db; border-radius: 6px; color: #4b5563; font-size: 12px; }
  .footer { margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 10px; font-size: 11px; color: #6b7280; text-align: center; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="page-shell">
  <div class="header">
    ${hospital.logo ? `<img src="${esc(hospital.logo)}" alt="Logo" />` : ''}
    <h1>${esc(hospital.name)}</h1>
    ${hospital.nameBn ? `<p class="bn">${esc(hospital.nameBn)}</p>` : ''}
    ${hospital.address ? `<p>${esc(hospital.address)}</p>` : ''}
    ${contact ? `<p>${esc(contact)}</p>` : ''}
  </div>

  <div class="title">PATIENT REGISTRATION SLIP</div>

  <table>
    ${row('ID', patient.registrationNumber)}
    ${row('Name', fullName)}
    ${row('Age / Gender', ageGender)}
    ${row('Blood Group', patient.bloodGroup)}
    ${row('Mobile', patient.mobileNumber)}
    ${row('Category', patientCategoryLabel(patient.patientCategory))}
    ${row('Registered On', fmtDate(patient.registrationDate))}
    ${addr ? row('Address', addr) : ''}
    ${slipAddressDetails.map((entry) => row(entry.label, entry.value)).join('')}
    ${row('Emergency Contact', [patient.emergencyContactName, patient.emergencyContactRelation].filter(Boolean).join(' · '))}
    ${row('Emergency Phone', patient.emergencyContactPhone)}
  </table>

  <div class="note">Please keep this slip for your records and present it at the reception / billing counter.</div>

  <div class="footer">
    ${hospital.name ? `<div>${esc(hospital.name)}</div>` : ''}
    ${hospital.certified ? `<div>${esc(hospital.certified)}</div>` : ''}
    <div>Printed on ${new Date().toLocaleString('en-GB')}</div>
  </div>
  </div>
</body>
</html>`;
}

/**
 * Wallet-sized patient card (85.6 × 54 mm) with the patient's key details.
 * Printed from the success modal; also the browser-print fallback.
 */
export function buildPatientCardHtml(patient: Patient, hospital: PatientPrintHospital): string {
  const esc = (value: unknown) => escapeHtml(value);
  const dash = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value).trim();
    return text.length > 0 ? esc(text) : '—';
  };
  const fullName = joinFullName(patient);

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Patient Card ${esc(patient.registrationNumber)}</title>
<style>
  * { box-sizing: border-box; }
  @page { size: 85.6mm 54mm; margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; }
  .card { width: 85.6mm; height: 54mm; padding: 6mm 5mm; display: flex; flex-direction: column; }
  .top { display: flex; align-items: center; gap: 6px; border-bottom: 1.5px solid #1f2937; padding-bottom: 4px; }
  .top img { height: 26px; width: 26px; object-fit: contain; flex-shrink: 0; }
  .top .meta { min-width: 0; flex: 1; }
  .top h1 { margin: 0; font-size: 12px; line-height: 1.1; }
  .top p { margin: 0; font-size: 7px; color: #4b5563; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .body { display: flex; gap: 6px; flex: 1; margin-top: 5px; }
  .photo { width: 20mm; height: 24mm; object-fit: cover; border: 1px solid #d1d5db; border-radius: 3px; }
  .photo-placeholder { width: 20mm; height: 24mm; border: 1px dashed #d1d5db; border-radius: 3px; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 7px; text-align: center; }
  .details { flex: 1; min-width: 0; }
  .details .name { font-size: 12px; font-weight: 700; margin: 0 0 2px; }
  .details .uhid { font-family: 'Courier New', monospace; font-size: 9px; color: #374151; margin-bottom: 4px; }
  .details table { width: 100%; border-collapse: collapse; }
  .details td { font-size: 8px; padding: 1px 0; vertical-align: top; }
  .details td.k { color: #6b7280; width: 42%; }
  .details td.v { font-weight: 600; }
  .footer { margin-top: 3px; font-size: 7px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 2px; }
</style>
</head>
<body>
  <div class="card">
    <div class="top">
      ${hospital.logo ? `<img src="${esc(hospital.logo)}" alt="Logo" />` : ''}
      <div class="meta">
        <h1>${esc(hospital.name)}</h1>
        ${hospital.address ? `<p>${esc(hospital.address)}</p>` : ''}
        ${hospital.hotline ? `<p>Hotline: ${esc(hospital.hotline)}</p>` : ''}
      </div>
    </div>
    <div class="body">
      ${patient.photoUrl
        ? `<img class="photo" src="${esc(patient.photoUrl)}" alt="Patient photo" />`
        : `<div class="photo-placeholder">No photo</div>`}
      <div class="details">
        <p class="name">${esc(fullName)}</p>
        <div class="uhid">ID: ${esc(patient.registrationNumber)}</div>
        <table>
          <tr><td class="k">Age / Gender</td><td class="v">${dash([patient.age ? `${patient.age} yrs` : '', patient.gender].filter(Boolean).join(' / '))}</td></tr>
          <tr><td class="k">Blood Group</td><td class="v">${dash(patient.bloodGroup)}</td></tr>
          <tr><td class="k">Mobile</td><td class="v">${dash(patient.mobileNumber)}</td></tr>
          <tr><td class="k">Emergency</td><td class="v">${dash(patient.emergencyContactPhone)}</td></tr>
        </table>
      </div>
    </div>
    <div class="footer">Please carry this card for all hospital visits.</div>
  </div>
</body>
</html>`;
}